import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { dataService } from '../services/dataService';
import { storageService } from '../services/storageService';

interface QuantFeedback {
  id: string;
  problemNumber: number;
  errorType: string; // Type of GRE problem
  errorMade: string; // Type of approach/strategy used
  difficulty: string; // Problem difficulty (Easy, Medium, Hard)
  notes: string;
  timestamp: number;
  date: string;
  image?: string; // Base64 encoded image data
}

interface QuantFeedbackTrackerProps {
  onComplete?: (feedback?: QuantFeedback) => void;
  onCancel?: () => void;
  defaultProblemNumber?: number;
}

const QuantFeedbackTracker: React.FC<QuantFeedbackTrackerProps> = ({ onComplete, onCancel, defaultProblemNumber }) => {
  const { user } = useAuth();
  const [feedbacks, setFeedbacks] = useState<QuantFeedback[]>([]);
  const [showForm, setShowForm] = useState(!!onComplete); // Auto-show form if called from problem tracker
  const [editingFeedback, setEditingFeedback] = useState<QuantFeedback | null>(null);
  
  // Form state
  const [problemNumberInput, setProblemNumberInput] = useState(
    defaultProblemNumber !== undefined ? defaultProblemNumber.toString() : ''
  );
  const [errorType, setErrorType] = useState('');
  const [errorMade, setErrorMade] = useState('');
  const [difficulty, setDifficulty] = useState('');
  const [notes, setNotes] = useState('');
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [showImageModal, setShowImageModal] = useState<string | null>(null);

  useEffect(() => {
    if (defaultProblemNumber !== undefined && showForm && !editingFeedback) {
      setProblemNumberInput(defaultProblemNumber.toString());
    }
  }, [defaultProblemNumber, showForm, editingFeedback]);

  // Predefined options (same as error tracker)
  const errorTypes = [
    'Algebra',
    'Geometry',
    'Arithmetic',
    'Data Interpretation',
    'Word Problems',
    'Number Properties',
    'Probability',
    'Statistics',
    'Sequences',
    'Functions',
    'Inequalities',
    'Exponents & Roots',
    'Combinations',
    'Other'
  ];

  // const errorMades = [
  //   'Careless mistake',
  //   'Forgot formula',
  //   'Forgot shortcut',
  //   'Misread question',
  //   'Calculation error',
  //   'Conceptual misunderstanding',
  //   'Time management',
  //   'Didn\'t check answer',
  //   'Misapplied formula',
  //   'Other'
  // ];

  const difficulties = [
    'Easy',
    'Medium',
    'Hard'
  ];

  // Image compression utility
  const compressImage = (base64String: string, maxWidth: number = 800, quality: number = 0.7): Promise<string> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;

        // Calculate new dimensions
        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }

        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Could not get canvas context'));
          return;
        }

        ctx.drawImage(img, 0, 0, width, height);
        
        // Convert to JPEG with quality setting
        const compressedBase64 = canvas.toDataURL('image/jpeg', quality);
        resolve(compressedBase64);
      };
      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = base64String;
    });
  };

  // Load feedbacks from localStorage on mount
  useEffect(() => {
    try {
      const savedFeedbacks = localStorage.getItem('gre_quant_feedback');
      if (savedFeedbacks) {
        const parsed = JSON.parse(savedFeedbacks);
        if (Array.isArray(parsed)) {
          const mapped = parsed.map((fb: any) => ({
            ...fb,
            problemNumber: typeof fb.problemNumber === 'number' ? fb.problemNumber : 0
          }));
          setFeedbacks(mapped);
        }
      }
    } catch (e) {
      console.error('Error loading feedbacks from localStorage:', e);
    }
  }, []);

  // Save feedbacks to localStorage whenever they change
  useEffect(() => {
    try {
      localStorage.setItem('gre_quant_feedback', JSON.stringify(feedbacks));
    } catch (e) {
      console.error('Error saving feedbacks to localStorage:', e);
    }
  }, [feedbacks]);

  useEffect(() => {
    if (!user) return;
    let isCancelled = false;

    const loadFromSupabase = async () => {
      try {
        const { data, error } = await dataService.quantFeedback.getAll(user.id);
        if (error || !data || isCancelled) {
          if (error) {
            console.error('Error loading feedbacks from Supabase:', error.message);
          }
          return;
        }
        const mapped = data.map((record: any) => ({
          id: record.id,
          problemNumber: record.problem_number ?? 0,
          errorType: record.error_type || '',
          errorMade: record.error_made || '',
          difficulty: record.difficulty || '',
          notes: record.notes || '',
          timestamp: record.timestamp,
          date: record.date,
          image: record.image || undefined
        }));
        setFeedbacks(mapped);
        try {
          localStorage.setItem('gre_quant_feedback', JSON.stringify(mapped));
        } catch (e) {
          console.error('Error caching Supabase feedbacks locally:', e);
        }
      } catch (e) {
        console.error('Unexpected error loading feedbacks from Supabase:', e);
      }
    };

    loadFromSupabase();

    return () => {
      isCancelled = true;
    };
  }, [user]);

  const uploadFeedbackImageIfNeeded = async (
    image?: string | null
  ): Promise<string | undefined> => {
    if (!user || !image || !image.startsWith('data:')) {
      return image || undefined;
    }
    try {
      const result = await storageService.uploadProblemImage(image, user.id, 'feedback');
      if (result?.error) {
        console.error('Feedback image upload failed:', result.error);
        return image || undefined;
      }
      return result?.publicUrl || result?.path || image || undefined;
    } catch (error) {
      console.error('Feedback image upload failed:', error);
      return image || undefined;
    }
  };
  const getImageSrc = (image?: string | null): string | undefined =>
    storageService.getPublicUrl(image);

  const syncFeedbackToSupabase = async (feedback: QuantFeedback, mode: 'insert' | 'update') => {
    if (!user) return;

    const preparedImage = await uploadFeedbackImageIfNeeded(feedback.image);
    if (preparedImage && preparedImage !== feedback.image) {
      setFeedbacks(prev =>
        prev.map(fb => (fb.id === feedback.id ? { ...fb, image: preparedImage } : fb))
      );
    }
    const imageForStorage = preparedImage || feedback.image;

    const payload = {
      user_id: user.id,
      problem_number: feedback.problemNumber,
      error_type: feedback.errorType || undefined,
      error_made: feedback.errorMade || undefined,
      difficulty: feedback.difficulty || undefined,
      notes: feedback.notes || undefined,
      image: imageForStorage || undefined,
      timestamp: feedback.timestamp,
      date: feedback.date
    };

    try {
      if (mode === 'update' && feedback.id.includes('-')) {
        const { error } = await dataService.quantFeedback.update(feedback.id, {
          problem_number: feedback.problemNumber,
          error_type: payload.error_type,
          error_made: payload.error_made,
          difficulty: payload.difficulty,
          notes: payload.notes,
          image: payload.image
        });
        if (error) {
          console.error('Error updating feedback in Supabase:', error.message);
        }
      } else {
        const { data, error } = await dataService.quantFeedback.insert(payload);
        if (error) {
          console.error('Error inserting feedback into Supabase:', error.message);
        } else if (data) {
          setFeedbacks(prev =>
            prev.map(fb => (fb.id === feedback.id ? { ...fb, id: data.id } : fb))
          );
        }
      }
    } catch (e) {
      console.error('Error syncing feedback with Supabase:', e);
    }
  };

  const handleImageCapture = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64String = reader.result as string;
        
        // Compress image before storing
        try {
          const compressedImage = await compressImage(base64String, 800, 0.7);
          setImagePreview(compressedImage);
        } catch (error) {
          // Fallback to original if compression fails
        setImagePreview(base64String);
        }
      };
      reader.onerror = () => {
        alert('Error reading image file');
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    setImagePreview(null);
    const fileInput = document.getElementById('feedback-image-input') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const resolvedProblemNumber = defaultProblemNumber ?? parseInt(problemNumberInput || '0', 10);
    if (!resolvedProblemNumber || Number.isNaN(resolvedProblemNumber)) {
      alert('Please enter a valid problem number');
      return;
    }

    if (!errorType || !difficulty) {
      alert('Please select Problem Type and Difficulty');
      return;
    }

    const today = new Date();
    const dateStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

    const newFeedback: QuantFeedback = {
      id: editingFeedback?.id || `temp-${Date.now()}`,
      problemNumber: resolvedProblemNumber,
      errorType,
      errorMade,
      difficulty,
      notes: notes.trim(),
      timestamp: editingFeedback?.timestamp || Date.now(),
      date: dateStr,
      image: imagePreview || editingFeedback?.image
    };

    if (user && newFeedback.image && newFeedback.image.startsWith('data:')) {
      const uploadedUrl = await uploadFeedbackImageIfNeeded(newFeedback.image);
      newFeedback.image = uploadedUrl || newFeedback.image;
    }

    if (editingFeedback) {
      setFeedbacks(prev => prev.map(fb => fb.id === editingFeedback.id ? newFeedback : fb));
      setEditingFeedback(null);
    } else {
      setFeedbacks(prev => [...prev, newFeedback]);
    }

    if (user) {
      syncFeedbackToSupabase(newFeedback, editingFeedback ? 'update' : 'insert');
    }

    // Reset form
    if (defaultProblemNumber === undefined) {
      setProblemNumberInput('');
    } else {
      setProblemNumberInput(defaultProblemNumber.toString());
    }
    setErrorType('');
    setErrorMade('');
    setDifficulty('');
    setNotes('');
    setImagePreview(null);
    setShowForm(false);
    const fileInput = document.getElementById('feedback-image-input') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
    
    // Call onComplete callback if provided, passing the feedback data
    if (onComplete) {
      onComplete(newFeedback);
    }
  };

  const handleEdit = (feedback: QuantFeedback) => {
    setEditingFeedback(feedback);
    setProblemNumberInput(feedback.problemNumber?.toString() || '');
    setErrorType(feedback.errorType);
    setErrorMade(feedback.errorMade);
    setDifficulty(feedback.difficulty || '');
    setNotes(feedback.notes);
    setImagePreview(getImageSrc(feedback.image) || feedback.image || null);
    setShowForm(true);
  };

  const handleDelete = (id: string) => {
    if (window.confirm('Are you sure you want to delete this feedback entry?')) {
      setFeedbacks(prev => prev.filter(fb => fb.id !== id));
      if (user && id.includes('-')) {
        dataService.quantFeedback.delete(id).catch(err => {
          console.error('Error deleting feedback from Supabase:', err);
        });
      }
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingFeedback(null);
    if (defaultProblemNumber === undefined) {
      setProblemNumberInput('');
    } else {
      setProblemNumberInput(defaultProblemNumber.toString());
    }
    setErrorType('');
    setErrorMade('');
    setDifficulty('');
    setNotes('');
    setImagePreview(null);
    const fileInput = document.getElementById('feedback-image-input') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
    // Call onCancel callback if provided
    if (onCancel) {
      onCancel();
    }
  };

  // Group feedbacks by date
  const feedbacksByDate = feedbacks.reduce((acc, feedback) => {
    if (!acc[feedback.date]) {
      acc[feedback.date] = [];
    }
    acc[feedback.date].push(feedback);
    return acc;
  }, {} as Record<string, QuantFeedback[]>);

  const sortedDates = Object.keys(feedbacksByDate).sort((a, b) => b.localeCompare(a));

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  return (
    <div style={{ padding: '10px', maxWidth: '800px', margin: '0 auto' }}>
      {!onComplete && (
        <h1 style={{ margin: '0 0 15px 0', fontSize: '24px' }}>
          ✅ Quant Feedback Tracker
        </h1>
      )}

      {/* Summary Stats */}
      {!onComplete && (
        <div style={{
          marginBottom: '15px',
          padding: '12px',
          backgroundColor: '#f8f9fa',
          borderRadius: '8px',
          display: 'flex',
          justifyContent: 'space-around',
          flexWrap: 'wrap',
          gap: '15px'
        }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#28a745' }}>
              {feedbacks.length}
            </div>
            <div style={{ fontSize: '12px', color: '#666' }}>Total Feedback</div>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      {!onComplete && (
        <div style={{
          display: 'flex',
          gap: '10px',
          justifyContent: 'center',
          flexWrap: 'wrap',
          marginBottom: '20px'
        }}>
          {!showForm && (
            <button
              onClick={() => setShowForm(true)}
              style={{
                padding: '12px 24px',
                fontSize: '16px',
                cursor: 'pointer',
                backgroundColor: '#28a745',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontWeight: 'bold'
              }}
            >
              + Add Feedback
            </button>
          )}
          {feedbacks.length > 0 && (
            <button
              onClick={async () => {
                if (window.confirm('Are you sure you want to delete all feedback entries? This cannot be undone.')) {
                  setFeedbacks([]);
                  if (user) {
                    try {
                      await dataService.quantFeedback.deleteAll(user.id);
                    } catch (err) {
                      console.error('Error deleting all feedback entries from Supabase:', err);
                    }
                  }
                }
              }}
              style={{
                padding: '12px 24px',
                fontSize: '16px',
                cursor: 'pointer',
                backgroundColor: '#dc3545',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontWeight: 'bold'
              }}
            >
              🗑️ Clear All
            </button>
          )}
        </div>
      )}

      {/* Add/Edit Form */}
      {showForm && (
        <div style={{
          marginBottom: '20px',
          padding: '20px',
          backgroundColor: '#fff',
          border: '2px solid #28a745',
          borderRadius: '8px'
        }}>
          <h2 style={{ marginTop: 0, marginBottom: '15px', fontSize: '20px' }}>
            {editingFeedback ? 'Edit Feedback Entry' : 'Add Feedback for Correct Answer'}
          </h2>
          <form onSubmit={handleSubmit}>
            {!defaultProblemNumber && (
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', fontSize: '14px' }}>
                  Problem Number *
                </label>
                <input
                  type="number"
                  min="1"
                  value={problemNumberInput}
                  onChange={(e) => setProblemNumberInput(e.target.value)}
                  required
                  style={{
                    width: '100%',
                    padding: '10px',
                    fontSize: '14px',
                    border: '1px solid #ccc',
                    borderRadius: '4px'
                  }}
                />
              </div>
            )}
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', fontSize: '14px' }}>
                Problem Type *
              </label>
              <select
                value={errorType}
                onChange={(e) => setErrorType(e.target.value)}
                required
                style={{
                  width: '100%',
                  padding: '10px',
                  fontSize: '14px',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  backgroundColor: '#fff'
                }}
              >
                <option value="">Select problem type...</option>
                {errorTypes.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>

            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', fontSize: '14px' }}>
                Approach/Strategy Used (Optional)
              </label>
              <textarea
                value={errorMade}
                onChange={(e) => setErrorMade(e.target.value)}
                placeholder="Describe the approach or strategy you used to solve this problem..."
                style={{
                  width: '100%',
                  minHeight: '80px',
                  padding: '10px',
                  fontSize: '14px',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  fontFamily: 'inherit',
                  resize: 'vertical'
                }}
              />
            </div>

            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', fontSize: '14px' }}>
                Problem Difficulty *
              </label>
              <select
                value={difficulty}
                onChange={(e) => setDifficulty(e.target.value)}
                required
                style={{
                  width: '100%',
                  padding: '10px',
                  fontSize: '14px',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  backgroundColor: '#fff'
                }}
              >
                <option value="">Select difficulty...</option>
                {difficulties.map(diff => (
                  <option key={diff} value={diff}>{diff}</option>
                ))}
              </select>
            </div>

            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', fontSize: '14px' }}>
                Notes (Optional)
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Add any additional notes about this problem..."
                style={{
                  width: '100%',
                  minHeight: '100px',
                  padding: '10px',
                  fontSize: '14px',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  fontFamily: 'inherit',
                  resize: 'vertical'
                }}
              />
            </div>

            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', fontSize: '14px' }}>
                Photo of Question (Optional)
              </label>
              <input
                id="feedback-image-input"
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handleImageCapture}
                style={{
                  width: '100%',
                  padding: '8px',
                  fontSize: '14px',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  backgroundColor: '#fff'
                }}
              />
              {imagePreview && (
                <div style={{ marginTop: '10px', position: 'relative' }}>
                  <img
                    src={imagePreview}
                    alt="Question preview"
                    style={{
                      maxWidth: '100%',
                      maxHeight: '300px',
                      borderRadius: '4px',
                      border: '1px solid #ccc',
                      display: 'block'
                    }}
                  />
                  <button
                    type="button"
                    onClick={handleRemoveImage}
                    style={{
                      position: 'absolute',
                      top: '5px',
                      right: '5px',
                      padding: '5px 10px',
                      fontSize: '12px',
                      cursor: 'pointer',
                      backgroundColor: '#dc3545',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      fontWeight: 'bold'
                    }}
                  >
                    Remove
                  </button>
                </div>
              )}
              {editingFeedback?.image && !imagePreview && (
                <div style={{ marginTop: '10px', position: 'relative' }}>
                  <p style={{ fontSize: '12px', color: '#666', marginBottom: '5px' }}>
                    Current image (upload new image to replace):
                  </p>
                  <img
                    src={editingFeedback.image}
                    alt="Current question"
                    style={{
                      maxWidth: '100%',
                      maxHeight: '300px',
                      borderRadius: '4px',
                      border: '1px solid #ccc',
                      display: 'block'
                    }}
                  />
                </div>
              )}
            </div>

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={handleCancel}
                style={{
                  padding: '10px 20px',
                  fontSize: '14px',
                  cursor: 'pointer',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px'
                }}
              >
                {onComplete ? 'Skip' : 'Cancel'}
              </button>
              <button
                type="submit"
                style={{
                  padding: '10px 20px',
                  fontSize: '14px',
                  cursor: 'pointer',
                  backgroundColor: '#28a745',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  fontWeight: 'bold'
                }}
              >
                {editingFeedback ? 'Update' : 'Add Feedback'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Feedback History */}
      {!onComplete && feedbacks.length === 0 && !showForm && (
        <div style={{
          padding: '40px',
          textAlign: 'center',
          backgroundColor: '#f8f9fa',
          borderRadius: '8px',
          color: '#666'
        }}>
          <p style={{ fontSize: '18px', margin: 0 }}>
            No feedback tracked yet. Click "Add Feedback" to start tracking!
          </p>
        </div>
      )}

      {!onComplete && feedbacks.length > 0 && (
        <div>
          <h2 style={{ fontSize: '20px', marginBottom: '15px' }}>
            Feedback History ({feedbacks.length} entries)
          </h2>
          {sortedDates.map(date => (
            <div key={date} style={{ marginBottom: '25px' }}>
              <h3 style={{
                fontSize: '16px',
                marginBottom: '10px',
                padding: '8px',
                backgroundColor: '#28a745',
                color: 'white',
                borderRadius: '4px',
                fontWeight: 'bold'
              }}>
                {formatDate(date)} ({feedbacksByDate[date].length} feedback{feedbacksByDate[date].length !== 1 ? 's' : ''})
              </h3>
              {feedbacksByDate[date]
                .sort((a, b) => b.timestamp - a.timestamp)
                .map(feedback => (
                <div
                  key={feedback.id}
                  style={{
                    marginBottom: '10px',
                    padding: '15px',
                    backgroundColor: '#fff',
                    border: '1px solid #dee2e6',
                    borderRadius: '6px',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ marginBottom: '5px' }}>
                        <span style={{
                          display: 'inline-block',
                          padding: '4px 8px',
                          backgroundColor: '#28a745',
                          color: 'white',
                          borderRadius: '4px',
                          fontSize: '12px',
                          fontWeight: 'bold',
                          marginRight: '8px'
                        }}>
                          {feedback.errorType}
                        </span>
                        {feedback.errorMade && (
                          <span style={{
                            display: 'inline-block',
                            padding: '4px 8px',
                            backgroundColor: '#17a2b8',
                            color: 'white',
                            borderRadius: '4px',
                            fontSize: '12px',
                            fontWeight: 'bold',
                            marginRight: '8px'
                          }}>
                            {feedback.errorMade}
                          </span>
                        )}
                        {feedback.difficulty && (
                          <span style={{
                            display: 'inline-block',
                            padding: '4px 8px',
                            backgroundColor: feedback.difficulty === 'Easy' ? '#17a2b8' : feedback.difficulty === 'Medium' ? '#ffc107' : '#dc3545',
                            color: 'white',
                            borderRadius: '4px',
                            fontSize: '12px',
                            fontWeight: 'bold'
                          }}>
                            {feedback.difficulty}
                          </span>
                        )}
                      </div>
                      {feedback.notes && (
                        <div style={{
                          marginTop: '8px',
                          padding: '8px',
                          backgroundColor: '#f8f9fa',
                          borderRadius: '4px',
                          fontSize: '13px',
                          color: '#495057',
                          whiteSpace: 'pre-wrap'
                        }}>
                          {feedback.notes}
                        </div>
                      )}
                      {(() => {
                        const imageSrc = getImageSrc(feedback.image);
                        if (!imageSrc) return null;
                        return (
                          <div style={{ marginTop: '10px' }}>
                            <img
                              src={imageSrc}
                              alt="Question"
                              onClick={() => setShowImageModal(imageSrc || null)}
                              style={{
                                maxWidth: '200px',
                                maxHeight: '200px',
                                borderRadius: '4px',
                                border: '1px solid #ccc',
                                cursor: 'pointer',
                                display: 'block'
                              }}
                            />
                            <p style={{ fontSize: '11px', color: '#666', marginTop: '4px' }}>
                              Click to view full size
                            </p>
                          </div>
                        );
                      })()}
                    </div>
                    <div style={{ display: 'flex', gap: '5px', marginLeft: '10px' }}>
                      <button
                        onClick={() => handleEdit(feedback)}
                        style={{
                          padding: '6px 12px',
                          fontSize: '12px',
                          cursor: 'pointer',
                          backgroundColor: '#ffc107',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px'
                        }}
                        title="Edit"
                      >
                        ✏️
                      </button>
                      <button
                        onClick={() => handleDelete(feedback.id)}
                        style={{
                          padding: '6px 12px',
                          fontSize: '12px',
                          cursor: 'pointer',
                          backgroundColor: '#dc3545',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px'
                        }}
                        title="Delete"
                      >
                        🗑️
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}

      {/* Image Modal */}
      {showImageModal && (
        <div
          onClick={() => setShowImageModal(null)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 2000,
            padding: '20px'
          }}
        >
          <div style={{ position: 'relative', maxWidth: '90%', maxHeight: '90%' }}>
            <button
              onClick={() => setShowImageModal(null)}
              style={{
                position: 'absolute',
                top: '-40px',
                right: '0',
                padding: '10px 20px',
                fontSize: '16px',
                cursor: 'pointer',
                backgroundColor: '#fff',
                color: '#333',
                border: 'none',
                borderRadius: '4px',
                fontWeight: 'bold'
              }}
            >
              ✕ Close
            </button>
            <img
              src={showImageModal}
              alt="Question full size"
              style={{
                maxWidth: '100%',
                maxHeight: '90vh',
                borderRadius: '4px',
                border: '2px solid #fff',
                display: 'block'
              }}
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default QuantFeedbackTracker;

